import { motion } from 'framer-motion';
import { fadeInUp } from '@/lib/animations';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface KpiCardProps {
  title: string;
  value: string | number;
  unit?: string;
  status?: 'good' | 'moderate' | 'critical' | 'neutral';
  alert?: boolean;
  sparklineData?: number[];
  trend?: 'up' | 'down' | 'flat';
  trendValue?: number;
  onClick?: () => void;
  delay?: number;
  limit?: number; // discharge standard limit (shown as small annotation)
  limitLabel?: string;
}

const statusColors: Record<string, { bg: string; text: string; border: string; glow: string }> = {
  good: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', glow: 'shadow-emerald-500/10' },
  moderate: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', glow: 'shadow-amber-500/10' },
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', glow: 'shadow-red-500/10' },
  neutral: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/20', glow: 'shadow-slate-500/5' },
};

function MiniSparkline({ data, color, title }: { data: number[]; color: string; title: string }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 60;
  const h = 30;
  const gradId = `spark-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-60">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${points} ${w},${h}`} fill={`url(#${gradId})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function KpiCard({
  title,
  value,
  unit = '',
  status = 'neutral',
  alert = false,
  sparklineData,
  trend,
  trendValue,
  onClick,
  delay = 0,
  limit,
  limitLabel = 'Limit',
}: KpiCardProps) {
  const colors = statusColors[status] || statusColors.neutral;
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-slate-500';

  const formattedValue = typeof value === 'number' ? value.toFixed(1) : value;
  const isOverLimit = limit != null && typeof value === 'number' && value > limit;
  const limitColor = isOverLimit ? 'text-red-400' : 'text-emerald-400';

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      transition={{ delay }}
      onClick={onClick}
      className={`rb-card p-4 cursor-pointer rb-card-hover relative ${alert ? 'border-red-500/40 shadow-[0_0_12px_rgba(239,68,68,0.15)]' : ''}`}
    >
      {alert && (
        <div className="absolute inset-0 rounded-xl border-2 border-red-500/30 animate-pulse pointer-events-none" />
      )}
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider">{title}</h3>
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${colors.bg} ${colors.text} ${colors.border}`}>
          {status === 'good' ? 'Good' : status === 'moderate' ? 'Moderate' : status === 'critical' ? 'Critical' : 'Neutral'}
        </span>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-baseline gap-1">
            <span className={`text-3xl font-bold tabular-nums ${alert ? 'text-red-400' : 'text-white'}`}>{formattedValue}</span>
            {unit && <span className="text-sm text-slate-500">{unit}</span>}
          </div>
          {limit != null && (
            <div className={`flex items-center gap-1 mt-1 ${limitColor}`}>
              <span className="text-[10px] font-medium">{limitLabel}: {limit} {unit}</span>
              {isOverLimit && <span className="text-[10px] px-1 py-0.5 rounded bg-red-500/15 border border-red-500/30">OVER</span>}
            </div>
          )}
          {trend && (
            <div className={`flex items-center gap-1 mt-1 ${trendColor}`}>
              <TrendIcon className="w-3 h-3" />
              <span className="text-xs">{trendValue != null ? `${trendValue > 0 ? '+' : ''}${trendValue}%` : ''}</span>
            </div>
          )}
        </div>
        {sparklineData && (
          <MiniSparkline data={sparklineData} color={alert ? '#ef4444' : status === 'good' ? '#10b981' : status === 'moderate' ? '#f59e0b' : status === 'critical' ? '#ef4444' : 'var(--rb-text-2)'} title={title} />
        )}
      </div>
    </motion.div>
  );
}
